import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError } from '../api/auth'
import {
  createFoodRecord,
  deleteFoodRecord,
  getFoodSummary,
  listFoodRecords,
  updateFoodRecord,
  type FoodRecord,
  type FoodSummary,
} from '../api/food'
import { getPreference, savePreference } from '../api/preferences'
import { AppShell } from '../layout/AppShell'
import { Icon } from '../components/Icon'
import { ModuleExportModal, ModuleRecordModal, type ModuleModalMode } from '../components/ModuleRecordModal'
import {
  ModuleSettingsModal,
  type ModulePreferences,
  type ModuleSettingsMode,
} from '../components/ModuleSettingsModal'
import { MODULE_CATALOG } from '../data/moduleCatalog'

type Props = { currentPath: string }
type ModalState = { mode: ModuleModalMode; id?: string }
type SavedSettings = { visibleColumns: boolean[]; preferences: ModulePreferences }

const module = MODULE_CATALOG.food
const DEFAULT_PREFERENCES: ModulePreferences = {
  pageSize: '25', density: 'Comfortable', autoRefresh: false,
  orientation: 'Landscape', paperSize: 'Letter', includeSummary: true,
}
const EMPTY_SUMMARY: FoodSummary = {
  mealsToday: 0, enrolledClients: 0, specialDiets: 0, missingCounts: 0, totalRecords: 0,
}

function toFeatureKey(label: string): string {
  return label.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function rowToInput(row: string[], feature: string) {
  const parsedQuantity = Number.parseInt(row[3] || '0', 10)
  return {
    feature,
    name: row[0] || '',
    details: row[1] || '',
    serviceTime: row[2] || '',
    quantity: Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : 0,
    status: row[4] || 'Draft',
  }
}

export function FoodPage({ currentPath }: Props) {
  const [activeSection, setActiveSection] = useState(0)
  const [activeFeature, setActiveFeature] = useState(module.sections[0]?.items[0] || '')
  const [query, setQuery] = useState('')
  const [recordQuery, setRecordQuery] = useState('')
  const [status, setStatus] = useState('All statuses')
  const [records, setRecords] = useState<FoodRecord[]>([])
  const [summary, setSummary] = useState<FoodSummary>(EMPTY_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modal, setModal] = useState<ModalState | null>(null)
  const [modalError, setModalError] = useState('')
  const [saving, setSaving] = useState(false)
  const [exportData, setExportData] = useState<{ filename: string; csv: string } | null>(null)
  const [settingsMode, setSettingsMode] = useState<ModuleSettingsMode | null>(null)
  const [visibleColumns, setVisibleColumns] = useState<boolean[]>(module.columns.map(() => true))
  const [preferences, setPreferences] = useState<ModulePreferences>({ ...DEFAULT_PREFERENCES })
  const [notice, setNotice] = useState('')

  const featureKey = toFeatureKey(activeFeature)

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getFoodSummary())
    } catch {
      setSummary(EMPTY_SUMMARY)
    }
  }, [])

  const loadRecords = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setRecords(await listFoodRecords(featureKey))
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to load Food records.')
    } finally {
      setLoading(false)
    }
  }, [featureKey])

  useEffect(() => {
    void loadRecords()
  }, [loadRecords])

  useEffect(() => {
    void loadSummary()
    void getPreference<SavedSettings>('module.food').then((saved) => {
      if (!saved) return
      if (Array.isArray(saved.visibleColumns) && saved.visibleColumns.length === module.columns.length) {
        setVisibleColumns(saved.visibleColumns)
      }
      setPreferences({ ...DEFAULT_PREFERENCES, ...(saved.preferences || {}) })
    }).catch(() => undefined)
  }, [loadSummary])

  useEffect(() => {
    if (!preferences.autoRefresh) return
    const timer = window.setInterval(() => {
      void loadRecords()
      void loadSummary()
    }, 30_000)
    return () => window.clearInterval(timer)
  }, [preferences.autoRefresh, loadRecords, loadSummary])

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    const section = module.sections[activeSection] || module.sections[0]
    return section.items.filter((item) => !q || item.toLowerCase().includes(q))
  }, [activeSection, query])

  const filteredRecords = useMemo(() => {
    const q = recordQuery.trim().toLowerCase()
    return records.filter((record) => {
      if (q && !record.values.some((cell) => cell.toLowerCase().includes(q))) return false
      if (status !== 'All statuses' && record.status !== status) return false
      return true
    })
  }, [records, recordQuery, status])

  const visibleRecords = useMemo(
    () => filteredRecords.slice(0, Math.max(Number(preferences.pageSize) || 25, 1)),
    [filteredRecords, preferences.pageSize],
  )
  const statusOptions = useMemo(
    () => Array.from(new Set(['All statuses', 'Draft', 'Published', 'Scheduled', 'Open', 'Active', 'Complete', 'Pending', 'Ready', 'In progress', 'Needs review', ...records.map((record) => record.status)])),
    [records],
  )

  const chooseSection = (index: number) => {
    setActiveSection(index)
    setActiveFeature(module.sections[index]?.items[0] || '')
    setQuery('')
    setRecordQuery('')
    setStatus('All statuses')
  }

  const chooseFeature = (feature: string) => {
    setActiveFeature(feature)
    setRecordQuery('')
    setStatus('All statuses')
  }

  const showNotice = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(''), 2400)
  }

  const saveRow = async (row: string[]) => {
    setError('')
    setModalError('')
    if (row[3] && (!/^\d+$/.test(row[3]) || Number(row[3]) < 0)) {
      setModalError('Count must be a whole number greater than or equal to 0.')
      return
    }
    setSaving(true)
    try {
      const existing = modal?.id ? records.find((record) => record.id === modal.id) : undefined
      if (existing) {
        await updateFoodRecord(existing.id, { ...rowToInput(row, featureKey), version: existing.version })
        showNotice(`${activeFeature} updated.`)
      } else {
        await createFoodRecord(rowToInput(row, featureKey))
        showNotice(`${activeFeature} added.`)
      }
      setModal(null)
      await Promise.all([loadRecords(), loadSummary()])
    } catch (reason) {
      setModalError(reason instanceof ApiError ? reason.message : 'Unable to save this Food record.')
    } finally {
      setSaving(false)
    }
  }

  const deleteRow = async () => {
    if (!modal?.id || !window.confirm(`Delete this ${activeFeature} record?`)) return
    setError('')
    try {
      await deleteFoodRecord(modal.id)
      setModal(null)
      showNotice(`${activeFeature} deleted.`)
      await Promise.all([loadRecords(), loadSummary()])
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to delete this Food record.')
    }
  }

  const exportCsv = () => {
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`
    const csv = [
      module.columns.map(escape).join(','),
      ...filteredRecords.map((record) => record.values.map(escape).join(',')),
    ].join('\n')
    setExportData({
      filename: `food-${featureKey}.csv`,
      csv,
    })
  }

  const persistSettings = async (nextColumns: boolean[], nextPreferences: ModulePreferences) => {
    await savePreference('module.food', { visibleColumns: nextColumns, preferences: nextPreferences })
  }

  const saveColumns = (nextColumns: boolean[]) => {
    setVisibleColumns(nextColumns)
    void persistSettings(nextColumns, preferences).catch(() => showNotice('Unable to save column settings.'))
    showNotice('Column settings saved.')
  }

  const savePreferences = (nextPreferences: ModulePreferences) => {
    setPreferences(nextPreferences)
    void persistSettings(visibleColumns, nextPreferences).catch(() => showNotice('Unable to save view settings.'))
    showNotice('View settings saved.')
  }

  const activeRecord = modal?.id ? records.find((record) => record.id === modal.id) : undefined
  const metrics = [
    { label: 'Meals today', value: String(summary.mealsToday), hint: 'Food schedule records' },
    { label: 'Enrolled clients', value: String(summary.enrolledClients), hint: 'Active CACFP enrollment' },
    { label: 'Special diets', value: String(summary.specialDiets), hint: 'Active plans' },
    { label: 'Missing counts', value: String(summary.missingCounts), hint: 'Needs review' },
  ]

  return (
    <AppShell
      currentPath={currentPath}
      title={module.title}
      subtitle={module.subtitle}
      search={{ value: query, onInput: setQuery, placeholder: 'Search food features…' }}
      actions={<button type="button" className="btn btn--primary btn--sm" onClick={() => setModal({ mode: 'create' })}><Icon name="plus" /> {module.action}</button>}
    >
      <div className="container module-page module-page--food">
        <section className="module-hero">
          <div className="module-hero__icon"><Icon name={module.icon} /></div>
          <div><span>Food module</span><h2>Food workspace</h2><p>{module.subtitle}</p></div>
          <div className="module-hero__badge"><span className="module-hero__pulse" />Database connected</div>
        </section>

        <section className="module-metrics" aria-label="Food summary">
          {metrics.map((metric, index) => (
            <article className={`module-metric module-metric--${index + 1}`} key={metric.label}>
              <span className="module-metric__icon"><Icon name={index === 0 ? 'food' : index === 1 ? 'users' : index === 2 ? 'chart' : 'bell'} /></span>
              <div><strong>{metric.value}</strong><span>{metric.label}</span><small>{metric.hint}</small></div>
            </article>
          ))}
        </section>

        <section className="module-toolbar" aria-label="Food tools">
          <div className="module-toolbar__label"><Icon name="admin" /><span>Tools</span></div>
          <button type="button" onClick={() => setSettingsMode('view')}><Icon name="admin" /> Settings</button>
          <button type="button" onClick={() => setSettingsMode('columns')}><Icon name="grid" /> Column Settings</button>
          <button type="button" onClick={() => setSettingsMode('print')}><Icon name="reports" /> Print Settings</button>
          <button type="button" onClick={() => setSettingsMode('print-preview')}><Icon name="reports" /> Print</button>
          <button type="button" onClick={exportCsv}><Icon name="reports" /> Export Excel</button>
        </section>

        <section className="module-workspace">
          <aside className="module-catalog">
            <div className="module-catalog__head"><span className="module-catalog__icon"><Icon name="food" /></span><div><b>Food</b><small>{module.sections.length} groups</small></div></div>
            <div className="module-section-tabs">
              {module.sections.map((section, index) => <button type="button" key={section.label} className={index === activeSection ? 'is-active' : ''} onClick={() => chooseSection(index)}>{section.label}<span>{section.items.length}</span></button>)}
            </div>
            <div className="module-feature-list">
              {visibleItems.map((item) => <button type="button" key={item} className={item === activeFeature ? 'is-active' : ''} onClick={() => chooseFeature(item)}><Icon name="chevron-right" size={15} /><span>{item}</span></button>)}
              {!visibleItems.length ? <p>No features match your search.</p> : null}
            </div>
          </aside>

          <div className="module-main">
            <div className="module-main__head">
              <div><span className="eyebrow">{module.sections[activeSection]?.label}</span><h2>{activeFeature}</h2></div>
              <div className="module-filter-row">
                <label><span>Find records</span><input type="search" aria-label="Find records" value={recordQuery} placeholder="Search table…" onInput={(event) => setRecordQuery((event.target as HTMLInputElement).value)} /></label>
                <label><span>Status</span><select aria-label="Filter status" value={status} onChange={(event) => setStatus((event.target as HTMLSelectElement).value)}>{statusOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
              </div>
            </div>
            {error ? <p className="form-error" style={{ margin: '0 1rem 1rem' }}>{error}</p> : null}
            <div className={`table-wrap module-table${preferences.density === 'Compact' ? ' module-table--compact' : ''}`}>
              <table className="data-table">
                <thead><tr>{module.columns.map((column, index) => visibleColumns[index] ? <th key={column}>{column}</th> : null)}<th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                <tbody>{visibleRecords.map((record) => <tr key={record.id}>
                  {record.values.map((cell, index) => visibleColumns[index] ? <td className={index > 1 ? 'muted' : ''} key={`${record.id}-${index}`}>{index === record.values.length - 1 ? <span className={`module-status module-status--${cell.toLowerCase().replace(/\s+/g, '-')}`}>{cell}</span> : cell || '—'}</td> : null)}
                  <td><div className="cell-actions"><button type="button" className="act-btn" title="View details" onClick={() => setModal({ mode: 'view', id: record.id })}><Icon name="eye" /></button><button type="button" className="act-btn" title="Edit" onClick={() => setModal({ mode: 'edit', id: record.id })}><Icon name="edit" /></button></div></td>
                </tr>)}</tbody>
              </table>
              {loading ? <div className="empty"><p>Loading Food records…</p></div> : !visibleRecords.length ? <div className="empty"><div><Icon name="search" /></div><p>No records match the current filters.</p></div> : null}
              <div className="list-foot"><span>Showing {visibleRecords.length} of {filteredRecords.length} matching records</span><span>{records.length} database records</span></div>
            </div>
          </div>
        </section>
      </div>

      {modal ? <ModuleRecordModal mode={modal.mode} moduleTitle="Food" featureTitle={activeFeature} columns={module.columns} row={activeRecord?.values} onClose={() => { setModal(null); setModalError('') }} onEdit={() => { setModalError(''); setModal((current) => current ? { ...current, mode: 'edit' } : null) }} onSave={(row) => void saveRow(row)} onDelete={modal.mode === 'edit' ? () => void deleteRow() : undefined} externalError={modalError} saving={saving} /> : null}
      {exportData ? <ModuleExportModal moduleTitle="Food" filename={exportData.filename} csv={exportData.csv} onClose={() => setExportData(null)} /> : null}
      {settingsMode ? <ModuleSettingsModal mode={settingsMode} moduleTitle="Food" featureTitle={activeFeature} columns={module.columns} visibleColumns={visibleColumns} preferences={preferences} rows={visibleRecords.map((record) => record.values)} onClose={() => setSettingsMode(null)} onSaveColumns={saveColumns} onSavePreferences={savePreferences} /> : null}
      {notice ? <div className="module-toast" role="status"><Icon name="check" /> {notice}</div> : null}
    </AppShell>
  )
}
